import { useVirtualizer } from '@tanstack/react-virtual';
import isNil from 'lodash/isNil';
import {
  type CSSProperties,
  type HTMLAttributes,
  type ReactElement,
  type Ref,
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';

const ESTIMATED_ROW_HEIGHT = 56;
const LISTBOX_MAX_HEIGHT = 336;
const OVERSCAN_COUNT = 5;
export const VIRTUAL_THRESHOLD = 60;

interface MeasurableElementProps {
  ref?: Ref<Element>;
  style?: CSSProperties;
  'data-index'?: number;
}

type MeasurableElement = ReactElement<MeasurableElementProps>;

interface VirtualListboxProps extends HTMLAttributes<HTMLElement> {
  scrollToIndex?: number;
}

export const VirtualListbox = forwardRef<HTMLDivElement, VirtualListboxProps>((props, ref) => {
  const { children, style: incomingStyle, scrollToIndex, ...other } = props;

  const flat = Children.toArray(children).filter(isValidElement) as MeasurableElement[];
  const itemCount = flat.length;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToIndexRef = useRef<boolean>(false);
  useImperativeHandle(ref, () => scrollRef.current as HTMLDivElement, []);

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN_COUNT,
  });

  useEffect(() => {
    if (hasScrolledToIndexRef.current || isNil(scrollToIndex) || scrollToIndex < 0 || scrollToIndex >= itemCount) {
      return undefined;
    }
    hasScrolledToIndexRef.current = true;
    const animationFrame = requestAnimationFrame(() => virtualizer.scrollToIndex(scrollToIndex, { align: 'center' }));
    return () => cancelAnimationFrame(animationFrame);
  }, [scrollToIndex, itemCount, virtualizer]);

  const totalSize = virtualizer.getTotalSize();
  const height = Math.min(totalSize > 0 ? totalSize : itemCount * ESTIMATED_ROW_HEIGHT, LISTBOX_MAX_HEIGHT);

  return (
    <div
      ref={scrollRef}
      {...other}
      style={{
        ...incomingStyle,
        height,
        maxHeight: LISTBOX_MAX_HEIGHT,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      <div style={{ height: totalSize, width: '100%', position: 'relative' }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const child = flat[virtualRow.index];
          return cloneElement(child, {
            key: virtualRow.key,
            ref: virtualizer.measureElement,
            'data-index': virtualRow.index,
            style: {
              ...(child.props.style ?? {}),
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
              boxSizing: 'border-box',
              overflow: 'hidden',
            },
          });
        })}
      </div>
    </div>
  );
});
VirtualListbox.displayName = 'VirtualListbox';
